import React from 'react'
import {
  Grid,
  Button,
  Paper,
  Table,
  TableHead,
  TableCell,
  TableBody,
  FormControl,
  InputLabel,
  Input,
  Typography,
  Divider,
} from '@material-ui/core'
import { connect } from 'react-redux'
import {
  GET_CATEGORIES,
  GET_CHECKLISTS,
  GET_CHECKLIST_BYID,
  UPDATE_CHECKLIST,
  ADD_CHECKLIST,
  DELETE_CHECKLIST,
} from './GraphQL'
import { useQuery, useLazyQuery, useMutation } from '@apollo/react-hooks'
import { startLoading, stopLoading } from '../../../actions/loading'
import { hasAccess } from '../../../utils/api'
import { formatErrors } from '../../../utils/common'

import ChecklistListItem from './ChecklistListItem'
import EditableHOCWrapper from '../../HOC/EditableHOC'

import { openErrorMessage, openSuccessMessage } from '../../../actions/message'
import AddChecklistModal from '../../../components/Modal/AddChecklistModal'
import EditChecklistModal from '../../../components/Modal/EditChecklistModal'
import ContainerWithTitle from '../../HOC/ContainerWithTitle'
import ModalDialog from '../../../components/common/ModalDialog'

const ChecklistListItemEditable = EditableHOCWrapper(ChecklistListItem)

let columns = ['Name', 'Category', 'Task Details', '']

const CheckLists = (props) => {
  const permitted = hasAccess()
  const addChecklistModal = React.useRef(null)
  const editChecklistModal = React.useRef(null)
  const deleteModal = React.useRef(null)
  const tasksModal = React.useRef(null)
  const [isUpdateCompleted, setIsUpdateCompleted] = React.useState(false)
  const [selectedItem, setSelectedItem] = React.useState({})
  const [selectedItemToDelete, setSelectedItemToDelete] = React.useState({})
  const [categoryList, setCategoryList] = React.useState([])
  const [checklistList, setChecklistList] = React.useState([])

  // eslint-disable-next-line no-unused-vars
  const resCategories = useQuery(GET_CATEGORIES, {
    onCompleted: (data) => {
      setCategoryList(data.categories)
      props.dispatch(stopLoading())
    },
    onError: (error) => {
      onError(formatErrors(error))
      props.dispatch(stopLoading())
    },
  })

  const [checkLists, resChecklists] = useLazyQuery(GET_CHECKLISTS, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data && data.checkLists && data.checkLists.values) {
        setChecklistList(data.checkLists.values)
      }

      props.dispatch(stopLoading())
    },
    onError: (error) => {
      onError(formatErrors(error))
      props.dispatch(stopLoading())
    },
  })

  const [addChecklist, resAddChecklist] = useMutation(ADD_CHECKLIST, {
    onCompleted: (data) => {
      props.dispatch(stopLoading())
      addChecklistModal.current.handleClose()
      onSuccess('New checklist saved')
      fetchApi()
    },
    onError: (error) => {
      onError(formatErrors(error))
      props.dispatch(stopLoading())
    },
  })

  const [updateChecklist, resUpdateChecklist] = useMutation(UPDATE_CHECKLIST, {
    onCompleted: (data) => {
      setIsUpdateCompleted(true)
      props.dispatch(stopLoading())
      onSuccess('Checklist updated')
      editChecklistModal.current.handleClose()
      fetchApi()
    },
    onError: (error) => {
      onError(formatErrors(error))
      props.dispatch(stopLoading())
    },
  })

  const [deleteChecklist, resDeleteChecklist] = useMutation(DELETE_CHECKLIST, {
    onCompleted: (data) => {
      props.dispatch(stopLoading())
      deleteModal.current.handleClose()
      onSuccess('Checklist deleted')
      fetchApi()
    },
    onError: (error) => {
      onError(formatErrors(error))
      props.dispatch(stopLoading())
    },
  })

  React.useEffect(() => {
    fetchApi()
  }, [])

  const fetchApi = () => {
    props.dispatch(startLoading())
    setChecklistList([])
    checkLists({
      variables: {},
    })
  }

  const onError = (message) => props.dispatch(openErrorMessage(message))
  const onSuccess = (message) => props.dispatch(openSuccessMessage(message))

  const validateChecklist = (data) => {
    if (!data.name) {
      onError('Please provide a valid checklist name')
      return false
    }
    if (!data.categoryId) {
      onError('Please provide a valid checklist category')
      return false
    }
    return true
  }

  const onSave = (data, afterOnSave) => {
    if (validateChecklist(data)) {
      setIsUpdateCompleted(false)
      const input = {
        id: data.id,
        name: data.name,
        categoryId: data.categoryId,
        taskDetails: data.taskDetails,
      }
      props.dispatch(startLoading())
      updateChecklist({ variables: { input } })
    }
  }

  const addChecklistApiCall = (data) => {
    props.dispatch(startLoading())
    addChecklist({ variables: { input: data } })
  }

  const updateChecklistApiCall = (data) => {
    props.dispatch(startLoading())
    updateChecklist({ variables: { input: data } })
  }

  const openEditModal = (item) => (e) => {
    setSelectedItem(item)
    editChecklistModal.current.handleClickOpen()
  }

  const openTasksModal = (item) => (e) => {
    setSelectedItem(item)
    tasksModal.current.handleClickOpen()
  }

  const onCloseTasksModal = (e) => {
    tasksModal.current.handleClose()
  }

  const openDeleteModal = (item) => (e) => {
    setSelectedItemToDelete(item)
    deleteModal.current.handleClickOpen()
  }

  const onDeleteItem = (e) => {
    props.dispatch(startLoading())
    deleteChecklist({ variables: { id: selectedItemToDelete.id } })
  }

  return (
    <div>
      <Grid container>
        <Grid className="addButton" item xs={12} sm={12} md={12} lg={12}>
          {permitted ? (
            <Button
              variant="raised"
              className="primary-button pull-right margin-bottom-small"
              onClick={() => addChecklistModal.current.handleClickOpen()}
            >
              Add Checklist
            </Button>
          ) : null}
        </Grid>
      </Grid>
      <Grid container>
        <Grid className="unversalScroll" item xs={12} sm={12} md={12} lg={12}>
          <Paper>
            <div className="table-scroll">
              <Table>
                <TableHead>
                  {columns && columns.map((col, index) => <TableCell key={index}>{col}</TableCell>)}
                </TableHead>
                <TableBody>
                  {checklistList &&
                    checklistList.length > 0 &&
                    checklistList.map((data, index) => (
                      <ChecklistListItemEditable
                        key={index}
                        dataToEdit={data}
                        onSave={onSave}
                        isUpdateCompleted={isUpdateCompleted}
                        onEdit={openEditModal(data)}
                        onSeeDetails={openTasksModal(data)}
                        onDelete={openDeleteModal(data)}
                        categoryList={categoryList}
                      />
                    ))}
                </TableBody>
              </Table>
            </div>
          </Paper>
        </Grid>
      </Grid>
      <AddChecklistModal
        ref={addChecklistModal}
        addChecklist={addChecklistApiCall}
        onError={onError}
        categoryList={categoryList}
      />
      <EditChecklistModal
        ref={editChecklistModal}
        updateChecklist={updateChecklistApiCall}
        onError={onError}
        item={selectedItem}
        categoryList={categoryList}
      />
      <ModalDialog ref={deleteModal} onConfirm={onDeleteItem} confirmText="delete">
        Are you sure you want to delete '{selectedItemToDelete.name}'
      </ModalDialog>
      <ModalDialog
        ref={tasksModal}
        onConfirm={onCloseTasksModal}
        confirmText="Close"
        cancelHidden
        title={'Task Details'}
      >
        {
          <Grid container>
            <Grid item xs={12} sm={12} md={12} lg={12} style={{ margin: '10px 0px' }}>
              <Typography>
                <b>Checklist name: </b> {selectedItem.name}{' '}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              {selectedItem.taskDetails &&
                selectedItem.taskDetails.map((task, index) => {
                  return (
                    <Grid container key={index} style={{ margin: '10px 0px' }}>
                      <Grid item sm={2}>
                        <FormControl fullWidth className="margin-bottom-small">
                          <InputLabel>Position</InputLabel>
                          <Input
                            name="position"
                            disabled
                            value={task.position}
                            fullWidth
                            inputProps={{
                              maxLength: 50,
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item sm={10}>
                        <FormControl fullWidth className="margin-bottom-small">
                          <InputLabel>Name</InputLabel>
                          <Input
                            name="name"
                            disabled
                            value={task.name}
                            fullWidth
                            inputProps={{
                              maxLength: 50,
                            }}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  )
                })}
            </Grid>
          </Grid>
        }
      </ModalDialog>
    </div>
  )
}

export default connect(({ dispatch, shopReducer }) => ({
  dispatch,
  shopReducer,
}))(ContainerWithTitle(CheckLists, 'CheckLists'))



// WEBPACK FOOTER //
// ./src/containers/Settings/Manufacturing/CheckLists.js